import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { fiat, fiatSign } from '@/shared/lib/format'
import { LineChart } from '@/shared/ui/LineChart'
import { Panel } from '@/shared/ui/Panel'
import { Section } from '@/shared/ui/Section'
import { Select } from '@/shared/ui/Select'
import { Strip } from '@/shared/ui/Strip'
import { Tile, toneOf } from '@/shared/ui/Tile'
import { changeNote, Coins, Withdrawals } from '@/pages/app/OverviewPage'
import { CHART_PERIODS } from '@/stores/CabinetStore'
import { useCabinet, useStores } from '@/stores/RootStore'
import styles from './Overview.module.css'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/** Значение селекта для «все аккаунты»: у Select нет null. */
const ALL = ''

/**
 * Админский обзор — по макету Overview 4:28811 (светлая) и 4:29762
 * (тёмная).
 *
 * Это ТОТ ЖЕ экран, что обзор мерчанта, с двумя отличиями: плитки
 * показывают комиссию и оборот шлюза вместо средств аккаунта, а у
 * графика дохода появляется фильтр «Все аккаунты». Всё ниже графика —
 * валюты, автовыводы, кошельки — совпадает с мерчантским подпись
 * в подпись и берётся из тех же компонентов: админ такой же аккаунт,
 * и его комиссия лежит на его же балансах.
 *
 * Очереди «на одобрение» здесь нет и в макете её нет: ручное одобрение
 * выводов выключено порогом на сервере. Журнал действий и
 * несопоставленные поступления остались на /app/admin/ops —
 * без ссылок из интерфейса, как диагностика на случай «деньги пришли,
 * а не зачислились».
 */
export const AdminOverviewPage = observer(function AdminOverviewPage() {
  useMeta('Обзор')
  const { admin: store } = useStores()
  const cabinet = useCabinet()

  useEffect(() => {
    void store.loadDashboard()
    void cabinet.loadHoldings()
  }, [store, cabinet])

  const overview = store.overview.value
  const view = store.view
  const sign = fiatSign(view)
  const accounts = store.accountOptions.value ?? []

  return (
    <>
      <Section title="Обзор">
        <div className={styles.tiles}>
          <Tile
            label="Баланс комиссии"
            value={overview ? fiat(overview.commission_balance, view) : '—'}
            loading={store.overview.initialLoading}
          />
          <Tile
            label="Комиссия за сегодня"
            value={overview ? fiat(overview.today_commission, view) : '—'}
            note={overview ? changeNote(overview.today_commission_change_percent) : undefined}
            tone={overview ? toneOf(overview.today_commission_change_percent) : undefined}
            loading={store.overview.initialLoading}
          />
          <Tile
            label="Оборот за сегодня"
            value={overview ? fiat(overview.today_turnover, view) : '—'}
            note={overview ? changeNote(overview.today_turnover_change_percent) : undefined}
            tone={overview ? toneOf(overview.today_turnover_change_percent) : undefined}
            loading={store.overview.initialLoading}
          />
        </div>

        <Panel className={styles.chartPanel}>
          <div className={styles.chartHead}>
            <h3 className={styles.chartTitle}>График дохода</h3>
            <div className={styles.chartControls}>
              <div className={styles.chartAccount}>
                <Select
                  ariaLabel="Аккаунт для графика"
                  placeholder="Все аккаунты"
                  options={[
                    { value: ALL, label: 'Все аккаунты' },
                    ...accounts.map((a) => ({ value: a.id, label: a.email })),
                  ]}
                  value={store.chartAccountId ?? ALL}
                  onChange={(value) => store.setChartAccount(value === ALL ? null : value)}
                />
              </div>
              <div className={styles.chartPeriod}>
                <Select
                  ariaLabel="Период графика"
                  placeholder="За неделю"
                  options={CHART_PERIODS.map((p) => ({ value: p.value, label: p.label }))}
                  value={String(store.chartDays)}
                  onChange={(value) => store.setChartDays(Number(value))}
                />
              </div>
            </div>
          </div>

          {store.chart.error && !store.chart.value ? (
            <Strip tone="warn">График не загрузился</Strip>
          ) : (
            <LineChart
              points={store.chart.value?.points ?? []}
              sign={sign}
              label="Комиссия шлюза по дням"
            />
          )}
        </Panel>
      </Section>

      <Coins />
      <Withdrawals />
    </>
  )
})
